import { type Dispatch, type SetStateAction, useState } from 'react'

import { FvLinkButton, Icon } from '@fv/client-components'
import type { FullShipment } from '@fv/client-types'

import { HasPermission, HasQuote } from '@/features/auth/HasPermission'
import { HideForVendor } from '@/features/auth/HasRole'
import { WorkflowEnabled } from '@/features/auth/HasWorkflow'
import DocumentList from '@/features/documents/DocumentList'
import { MiniQuoteList } from '@/features/mini-quote-list/MiniQuoteList'
import { useCarrierPublicSettings } from '@/hooks/useCarrierPublicSettings'

import { DividedContent } from '../../DividedContent'
import InfoBox from '../../InfoBox'
import { Pinline } from '../../Pinline'
import { CancelationMessage } from '../../shared/CancelationMessage'
import { DetailPanelLayout } from '../DetailPanelLayout'
import type { PanelName } from '../LoadDetailPanel'
import ShipmentActions from '../ShipmentActions'
import { ActivePanelFooter } from './ActivePanelFooter'
import {
  ActivePanelHeaderBody,
  ActivePanelHeaderNav,
} from './ActivePanelHeader'

type ActiveLoadDetailProps = {
  load: FullShipment
  setActivePanel: Dispatch<SetStateAction<PanelName>>
}

export const ActiveLoadDetail = ({
  load,
  setActivePanel,
}: ActiveLoadDetailProps) => {
  const [isShowingRates, setShowingRates] = useState(false)

  const { bol, documents, pickup, selectedQuote, status } = load

  const isCanceled = status === 'canceled'
  const isUpdated = !!bol?.changedAfterDispatch
  const isApiDispatch = pickup?.method === 'api'
  const { carrierPublicSettings } = useCarrierPublicSettings(
    selectedQuote?.carrierId,
  )

  return (
    <DetailPanelLayout
      header={
        isShowingRates ? (
          <ActivePanelHeaderNav
            isShowingRates={isShowingRates}
            setShowingRates={setShowingRates}
          />
        ) : undefined
      }
      footer={
        <WorkflowEnabled workflow={load.workflow}>
          <ActivePanelFooter
            isShowingRates={isShowingRates}
            load={load}
            setShowingRates={setShowingRates}
          />
        </WorkflowEnabled>
      }
    >
      {isCanceled && (
        <InfoBox>
          <CancelationMessage
            carrierSettings={carrierPublicSettings}
            load={load}
          />
        </InfoBox>
      )}

      {isUpdated && !isCanceled && isApiDispatch && (
        <InfoBox>
          Your bill of lading has been updated. You need to call{' '}
          {carrierPublicSettings?.dispatchPhone && (
            <FvLinkButton href={`tel:${carrierPublicSettings.dispatchPhone}`}>
              {carrierPublicSettings.dispatchPhone}
            </FvLinkButton>
          )}{' '}
          and inform the carrier of your changes.
        </InfoBox>
      )}

      <ActivePanelHeaderBody load={load} setActivePanel={setActivePanel} />

      {isShowingRates && (
        <div className="shipment-list-pricing">
          <MiniQuoteList key={selectedQuote?._id} load={load} />
        </div>
      )}

      {!isShowingRates && (
        <ShipmentActions
          setActivePanel={setActivePanel}
          shipment={load}
          showRatesPanel={() => setShowingRates(true)}
        />
      )}

      {!isShowingRates && (
        <div className="shipment-list-documents-ctn">
          <DividedContent>
            <p className="mb-0">
              <Icon icon="file" className="text-fv-gray-dark w-5" />
              <span>Shipment docs</span>
            </p>
            {!isCanceled && (
              <HideForVendor>
                <HasQuote>
                  <FvLinkButton
                    icon="share"
                    onClick={() => setActivePanel('share')}
                  >
                    Share
                  </FvLinkButton>
                </HasQuote>
              </HideForVendor>
            )}

            <HasPermission name="load-file-manager">
              <FvLinkButton
                icon="upload"
                onClick={() => setActivePanel('upload')}
              >
                Add / Delete
              </FvLinkButton>
            </HasPermission>
          </DividedContent>
          <Pinline className="mt-2 mb-4" />
          <DocumentList documents={documents} />
        </div>
      )}
    </DetailPanelLayout>
  )
}
